import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { OrganizationStaff } from '@/shared/api/protocol-ts/model/dto_organization_staff_pb';
import { LoadingStateType, SliceName } from '@/shared/config';

export const organizationStaffAdapter = createEntityAdapter<OrganizationStaff>({
  selectId: (staff) => staff.ID,
});

const initialState = {
  loading: 'idle' as LoadingStateType,
};

const organizationStaffState =
  organizationStaffAdapter.getInitialState(initialState);

type OrganizationStaffState = typeof organizationStaffState;

const organizationStaffSlice = createSlice({
  name: SliceName.organizationStaff,
  initialState: organizationStaffState,
  reducers: {
    setOne: (state, action: PayloadAction<OrganizationStaff>) => {
      organizationStaffAdapter.setOne(
        state as OrganizationStaffState,
        action.payload,
      );
    },
    setMany: (state, action: PayloadAction<OrganizationStaff[]>) => {
      organizationStaffAdapter.setMany(
        state as OrganizationStaffState,
        action.payload,
      );
    },
    removeByOrganizationID: (state, action: PayloadAction<string>) => {
      const idsToRemove = Object.values(state.entities)
        .filter((staff) => staff?.OrganizationID === action.payload)
        .map((staff) => staff!.ID);

      organizationStaffAdapter.removeMany(
        state as OrganizationStaffState,
        idsToRemove,
      );
    },
    setNewestOne: (state, action: PayloadAction<OrganizationStaff>) => {
      const currentRevisionNumber =
        state.entities[action.payload.ID]?.Revision?.Number ?? 0;
      const payloadRevisionNumber = action.payload.Revision?.Number ?? 0;

      if (payloadRevisionNumber > currentRevisionNumber) {
        organizationStaffAdapter.setOne(
          state as OrganizationStaffState,
          action.payload,
        );
      }
    },
    removeAll: (state) => {
      organizationStaffAdapter.removeAll(state as OrganizationStaffState);
    },
    setLoading: (state, action: PayloadAction<LoadingStateType>) => {
      state.loading = action.payload;
    },
  },
});

export const { actions } = organizationStaffSlice;

export default organizationStaffSlice.reducer;
